/* KeepMySnacks theme — vanilla JS, no dependencies. */
(() => {
  const $ = (sel, root = document) => root.querySelector(sel);
  const $$ = (sel, root = document) => [...root.querySelectorAll(sel)];

  /* ---------- Money ---------- */

  function formatMoney(cents, format = window.theme?.moneyFormat || "{{amount}}") {
    const value = Number(cents) || 0;
    const fmt = (precision, thousands = ",", decimal = ".") => {
      const [whole, frac] = (value / 100).toFixed(precision).split(".");
      return whole.replace(/\B(?=(\d{3})+(?!\d))/g, thousands) + (frac ? decimal + frac : "");
    };
    const text = format.replace(/<[^>]*>/g, "");
    return text.replace(/\{\{\s*(\w+)\s*\}\}/, (_, key) => {
      switch (key) {
        case "amount_no_decimals":
          return fmt(0);
        case "amount_with_comma_separator":
          return fmt(2, ".", ",");
        case "amount_no_decimals_with_comma_separator":
          return fmt(0, ".", ",");
        case "amount_with_apostrophe_separator":
          return fmt(2, "'", ".");
        case "amount_with_space_separator":
          return fmt(2, " ", ",");
        default:
          return fmt(2);
      }
    });
  }

  /* ---------- Sticky header ---------- */

  const header = $("[data-sticky-header]")?.closest(".shopify-section") || $("[data-sticky-header]");
  if (header) {
    const update = () => header.toggleAttribute("data-scrolled", window.scrollY > 8);
    update();
    window.addEventListener("scroll", update, { passive: true });
  }

  /* ---------- Drawers (menu + cart) ---------- */

  function drawer(root, panelSel, overlaySel) {
    const panel = $(panelSel, root);
    const overlay = $(overlaySel, root);
    return {
      open() {
        root.inert = false;
        root.classList.remove("pointer-events-none");
        panel.classList.remove("translate-x-full");
        overlay.classList.add("opacity-100");
        document.body.style.overflow = "hidden";
      },
      close() {
        root.inert = true;
        root.classList.add("pointer-events-none");
        panel.classList.add("translate-x-full");
        overlay.classList.remove("opacity-100");
        document.body.style.overflow = "";
      },
    };
  }

  const menuRoot = $("[data-menu]");
  if (menuRoot) {
    const menu = drawer(menuRoot, "[data-menu-panel]", "[data-menu-overlay]");
    $$("[data-menu-open]").forEach((b) => b.addEventListener("click", () => menu.open()));
    $$("[data-menu-close]", menuRoot).forEach((b) => b.addEventListener("click", () => menu.close()));
    document.addEventListener("keydown", (e) => e.key === "Escape" && menu.close());
  }

  /* ---------- Cart ---------- */

  const cartRoot = $("[data-cart-drawer]");
  const cartDrawer = cartRoot && drawer(cartRoot, "[data-cart-panel]", "[data-cart-overlay]");

  async function fetchCart() {
    const res = await fetch(window.theme.routes.cart, { headers: { Accept: "application/json" } });
    return res.json();
  }

  function renderCart(cart) {
    $$("[data-cart-count]").forEach((el) => {
      el.textContent = cart.item_count;
      el.classList.toggle("hidden", cart.item_count === 0);
    });
    if (!cartRoot) return;

    const empty = cart.item_count === 0;
    $("[data-cart-empty]", cartRoot).classList.toggle("hidden", !empty);
    $("[data-cart-filled]", cartRoot).classList.toggle("hidden", empty);
    $("[data-cart-filled]", cartRoot).classList.toggle("flex", !empty);
    if (empty) return;

    const threshold = window.theme.freeShippingThreshold || 0;
    const shipText = $("[data-cart-shipping-text]", cartRoot);
    if (shipText && threshold > 0) {
      const left = threshold - cart.total_price;
      const region = window.theme.freeShippingRegion ? ` ${window.theme.freeShippingRegion}` : "";
      shipText.innerHTML =
        left > 0
          ? window.theme.strings.awayFromFree.replace("[amount]", formatMoney(left)).replace("[where]", region)
          : window.theme.strings.unlockedFree.replace("[where]", region);
      $("[data-cart-shipping-bar]", cartRoot).style.width = `${Math.min(100, (cart.total_price / threshold) * 100)}%`;
    }

    const list = $("[data-cart-lines]", cartRoot);
    const template = $("[data-cart-line-template]");
    list.replaceChildren(
      ...cart.items.map((item) => {
        const li = template.content.firstElementChild.cloneNode(true);
        const img = $("[data-line-image]", li);
        if (item.image) img.src = item.image.replace(/(\.[a-z]+)(\?|$)/i, "_160x$1$2");
        else img.remove();
        $("[data-line-title]", li).textContent = item.product_title;
        $("[data-line-variant]", li).textContent = item.variant_title && item.variant_title !== "Default Title" ? item.variant_title : "";
        $("[data-line-qty]", li).textContent = item.quantity;
        $("[data-line-price]", li).textContent = formatMoney(item.final_line_price);
        $("[data-line-minus]", li).addEventListener("click", () => changeLine(item.key, item.quantity - 1));
        $("[data-line-plus]", li).addEventListener("click", () => changeLine(item.key, item.quantity + 1));
        return li;
      }),
    );
    $("[data-cart-subtotal]", cartRoot).textContent = formatMoney(cart.total_price);
  }

  async function changeLine(key, quantity) {
    const res = await fetch(window.theme.routes.cartChange, {
      method: "POST",
      headers: { "Content-Type": "application/json", Accept: "application/json" },
      body: JSON.stringify({ id: key, quantity: Math.max(0, quantity) }),
    });
    renderCart(await res.json());
  }

  async function openCart() {
    renderCart(await fetchCart());
    cartDrawer?.open();
  }

  if (cartRoot) {
    $$("[data-cart-open]").forEach((b) => b.addEventListener("click", openCart));
    $$("[data-cart-close]", cartRoot).forEach((b) => b.addEventListener("click", () => cartDrawer.close()));
    document.addEventListener("keydown", (e) => e.key === "Escape" && cartDrawer.close());
  }

  /* ---------- Countdowns ---------- */

  // Offer days are inclusive and run on US Eastern winter time, like the calendar in theme settings.
  const offers = (window.theme?.promoSchedule || []).map((offer) => ({
    name: offer.name,
    start: Date.parse(`${offer.start}T00:00:00-05:00`),
    end: Date.parse(`${offer.end}T00:00:00-05:00`) + 86400000,
  }));
  const countdowns = $$("[data-countdown]");
  const units = { d: [86400000, Infinity], h: [3600000, 24], m: [60000, 60], s: [1000, 60] };
  let current = null;

  const showOffer = (offer) => {
    current = offer;
    countdowns.forEach((el) => {
      el.hidden = !offer;
      if (!offer) return;
      const name = $("[data-offer-name]", el);
      if (name) name.textContent = offer.name;
      const endsIn = $("[data-offer-ends-in]", el);
      if (endsIn) endsIn.textContent = (window.theme?.strings?.offerEndsIn || "[name]").replace("[name]", offer.name);
      const date = $("[data-countdown-date]", el);
      if (date) {
        date.textContent = new Date(offer.end - 1).toLocaleDateString(document.documentElement.lang || "en-US", {
          month: "short",
          day: "numeric",
          timeZone: "America/New_York",
        });
      }
    });
  };

  const tickCountdowns = () => {
    const now = Date.now();
    if (!current || now >= current.end) {
      showOffer(offers.find((offer) => now >= offer.start && now < offer.end) || null);
    }
    if (!current) return;
    const left = current.end - now;
    countdowns.forEach((el) => {
      $$("[data-unit]", el).forEach((cell) => {
        const [ms, mod] = units[cell.dataset.unit];
        cell.textContent = String(Math.floor(left / ms) % mod).padStart(2, "0");
      });
    });
  };

  if (countdowns.length && offers.length) {
    tickCountdowns();
    setInterval(tickCountdowns, 1000);
  }

  /* ---------- Before / after slider ---------- */

  $$("[data-before-after]").forEach((fig) => {
    const input = $("input[type=range]", fig);
    const before = $("[data-before]", fig);
    const handle = $("[data-handle]", fig);
    input.addEventListener("input", () => {
      before.style.clipPath = `inset(0 ${100 - input.value}% 0 0)`;
      handle.style.left = `${input.value}%`;
    });
  });

  /* ---------- Product: gallery ---------- */

  const product = $("[data-product]");
  if (product) {
    const main = $("[data-gallery-main]", product);
    const thumbs = $$("[data-gallery-thumb]", product);
    let current = 0;
    const show = (i) => {
      if (!thumbs.length) return;
      current = (i + thumbs.length) % thumbs.length;
      main.src = thumbs[current].dataset.src;
      main.alt = thumbs[current].dataset.alt;
      thumbs.forEach((t, j) => {
        t.classList.toggle("ring-ink", j === current);
        t.classList.toggle("ring-transparent", j !== current);
      });
    };
    thumbs.forEach((t, i) => t.addEventListener("click", () => show(i)));
    $$("[data-gallery-step]", product).forEach((b) =>
      b.addEventListener("click", () => show(current + Number(b.dataset.galleryStep))),
    );

    /* ---------- Product: offer picker ---------- */

    const offers = $$("[data-offer]", product);
    const pack = $("[data-pack]", product);

    function packState() {
      const slots = $$("[data-pack-slot]", pack);
      const order = $$("[data-variant]", slots[0]).map((b) => b.dataset.color);
      const picks = slots.map((slot) => $("[aria-checked=true]", slot) || $("[data-variant]", slot));
      const colors = picks.map((p) => p.dataset.color).sort((a, b) => order.indexOf(a) - order.indexOf(b));
      // The 2-pack product has one variant per combination, titled e.g. "White + Blue".
      const variant = JSON.parse(pack.dataset.packVariants || "{}")[colors.join(" + ")];
      const counts = new Map();
      colors.forEach((c) => counts.set(c, (counts.get(c) || 0) + 1));
      const tag = [...counts].map(([c, n]) => `${n} ${c}`).join(" + ");
      const separately = picks.reduce((sum, p) => sum + Number(p.dataset.variantPrice), 0);
      return {
        lines: variant ? [{ id: variant.id, quantity: 1 }] : [],
        tag,
        separately,
        thumb: counts.size === 1 ? picks[0].dataset.thumb : $("[data-pack-thumb]", pack).dataset.mixed,
        available: Boolean(variant?.available),
      };
    }

    function selected() {
      const card = offers.find((o) => $("input[type=radio]", o)?.checked) || offers[0];
      if (!card) return null;
      if (card === pack) {
        const s = packState();
        const price = Number(pack.dataset.price);
        return { card, ...s, price, compare: s.separately > price ? s.separately : 0 };
      }
      return {
        card,
        lines: JSON.parse(card.dataset.lines),
        price: Number(card.dataset.price),
        compare: Number(card.dataset.compare),
        thumb: card.dataset.thumb,
        available: card.dataset.available === "true",
      };
    }

    function refresh() {
      const sel = selected();
      if (!sel) return;
      offers.forEach((o) => {
        const details = $("[data-offer-details]", o);
        if (details) details.classList.toggle("hidden", o !== sel.card);
      });
      if (pack) {
        const s = packState();
        $("[data-pack-tag]", pack).textContent = s.tag;
        $("[data-pack-thumb]", pack).src = s.thumb;
        const price = Number(pack.dataset.price);
        $("[data-pack-compare]", pack).textContent = s.separately > price ? formatMoney(s.separately) : "";
      }
      $("[data-price-current]", product).textContent = formatMoney(sel.price);
      $("[data-price-compare]", product).textContent = sel.compare ? formatMoney(sel.compare) : "";
      const save = $("[data-price-save]", product);
      save.classList.toggle("hidden", !sel.compare);
      if (sel.compare) save.textContent = window.theme.strings.save.replace("[percent]", Math.round((1 - sel.price / sel.compare) * 100));
      $("[data-in-stock]", product)?.classList.toggle("hidden", !sel.available);
      $$("[data-add-to-cart]", product).forEach((b) => {
        b.disabled = !sel.available;
        if (!b.dataset.label) b.dataset.label = b.textContent.trim();
        b.textContent = sel.available ? b.dataset.label : window.theme.strings.soldOut;
      });
      const stickyThumb = $("[data-sticky-thumb]", product);
      if (stickyThumb && sel.thumb) stickyThumb.src = sel.thumb;
      $("[data-sticky-price]", product).textContent = formatMoney(sel.price);
      $("[data-sticky-compare]", product).textContent = sel.compare ? formatMoney(sel.compare) : "";
    }

    offers.forEach((o) => $("input[type=radio]", o)?.addEventListener("change", refresh));
    if (pack) {
      $$("[data-pack-slot]", pack).forEach((slot) => {
        $$("[data-variant]", slot).forEach((btn) =>
          btn.addEventListener("click", () => {
            $$("[data-variant]", slot).forEach((b) => b.setAttribute("aria-checked", String(b === btn)));
            refresh();
          }),
        );
      });
    }
    refresh();

    /* ---------- Product: add to cart ---------- */

    const error = $("[data-cart-error]", product);
    $$("[data-add-to-cart]", product).forEach((button) =>
      button.addEventListener("click", async () => {
        const sel = selected();
        if (!sel || !sel.available) return;
        const buttons = $$("[data-add-to-cart]", product);
        buttons.forEach((b) => {
          b.disabled = true;
          b.textContent = "Adding…";
        });
        try {
          const res = await fetch(window.theme.routes.cartAdd, {
            method: "POST",
            headers: { "Content-Type": "application/json", Accept: "application/json" },
            body: JSON.stringify({ items: sel.lines }),
          });
          const data = await res.json();
          if (!res.ok) throw new Error(data.description || data.message || window.theme.strings.addError);
          error.classList.add("hidden");
          await openCart();
        } catch (err) {
          error.textContent = err.message;
          error.classList.remove("hidden");
        } finally {
          refresh();
        }
      }),
    );

    /* ---------- Product: sticky add-to-cart bar ---------- */

    const sticky = $("[data-sticky-buy]", product);
    const mainButton = $("[data-add-to-cart]", product);
    if (sticky && mainButton) {
      new IntersectionObserver(([entry]) => {
        const visible = !entry.isIntersecting && entry.boundingClientRect.top < 0;
        sticky.toggleAttribute("data-visible", visible);
        sticky.inert = !visible;
      }).observe(mainButton);
    }
  }

  /* ---------- Reviews: show more ---------- */

  $$("[data-reviews-more]").forEach((button) => {
    const list = button.closest("section").querySelector("[data-reviews]");
    button.addEventListener("click", () => {
      const hidden = $$("li[hidden]", list).slice(0, 4);
      hidden.forEach((li, i) => {
        li.hidden = false;
        li.style.setProperty("--delay", `${i * 0.08}s`);
        li.classList.add("motion-intro");
      });
      if (!$("li[hidden]", list)) button.parentElement.remove();
    });
  });

  /* ---------- Country & currency: follow the visitor's location until they pick one ---------- */

  // A manual pick (country or language) wins for the rest of the browser session;
  // otherwise every visit follows the visitor's location, re-checked every 10 minutes.
  const pickManually = (select) =>
    select.addEventListener("change", () => {
      try {
        sessionStorage.setItem("kms-picked", "1");
      } catch {}
      select.form.submit();
    });
  $$("[data-language-select]").forEach(pickManually);

  const countrySelect = $("[data-country-select]");
  if (countrySelect) {
    pickManually(countrySelect);
    (async () => {
      try {
        if (sessionStorage.getItem("kms-picked")) return;
        let geo = JSON.parse(sessionStorage.getItem("kms-geo") || "null");
        if (!geo || Date.now() - geo.at > 10 * 60 * 1000) {
          const res = await fetch("/browsing_context_suggestions.json?country[enabled]=true", { cache: "no-store" });
          geo = { code: (await res.json())?.detected_values?.country?.handle, at: Date.now() };
          sessionStorage.setItem("kms-geo", JSON.stringify(geo));
        }
        if (!geo.code || !window.theme.countries?.includes(geo.code)) return;
        const language = window.theme.countryLanguages?.[geo.code] || "en";
        if (geo.code === window.theme.country && language === window.theme.locale) return;
        countrySelect.value = geo.code;
        const localeInput = Object.assign(document.createElement("input"), { type: "hidden", name: "locale_code", value: language });
        countrySelect.form.append(localeInput);
        countrySelect.form.submit();
      } catch {}
    })();
  }

  /* ---------- Video strip: only play while on screen ---------- */

  $$("[data-video-strip]").forEach((strip) => {
    const videos = $$("video", strip);
    new IntersectionObserver(([entry]) => {
      videos.forEach((v) => (entry.isIntersecting ? v.play().catch(() => {}) : v.pause()));
    }).observe(strip);
  });
})();
